import { memo, useCallback, type MouseEventHandler, type ReactElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

export interface SelectorOption<V> {
  value: V;
  label: string;
  description?: ReactElement;
}

export interface GaugeSelectorOptionProps<V> {
  style?: object;
  option: SelectorOption<V>;
  isActive?: boolean;
  isHovered?: boolean;
  isCurrent?: boolean;
  disabled?: boolean;
  onChange?: (value: V) => void;
  onHover?: (value: V) => void;
  onHoverEnd?: MouseEventHandler<HTMLDivElement>;
}

export const GaugeSelectorOption = <V,>({
  style,
  option,
  isActive,
  isHovered,
  isCurrent,
  disabled,
  onChange,
  onHover,
  onHoverEnd,
}: GaugeSelectorOptionProps<V>) => {
  const onClick = useCallback(() => {
    onChange(option.value);
  }, [option, onChange]);

  const handleHover = useCallback(() => {
    if (typeof onHover === 'function') {
      onHover(option.value);
    }
  }, [option, onHover]);

  if (!option) {
    return null;
  }

  const className = classnames({
    'gauge-selector__option': true,
    'gauge-selector__option_active': isActive,
    'gauge-selector__option_hovered': isHovered,
    'gauge-selector__option_current': isCurrent,
    'gauge-selector__option_disabled': disabled,
  });

  const role = disabled ? 'presentation' : 'button';

  return (
    <div
      tabIndex={0}
      role={role}
      style={style}
      className={className}
      onClick={onClick}
      onMouseEnter={handleHover}
      onMouseLeave={onHoverEnd}
    >
      <div className="gauge-selector__option-gauge" title={option.label} />

      {option.label && <div className="gauge-selector__option-title">{option.label}</div>}

      {option.description && <div className="gauge-selector__option-description">{option.description}</div>}
    </div>
  );
};

export const GaugeSelectorOptionPropType = PropTypes.shape({
  value: PropTypes.number.isRequired,
  lable: PropTypes.string,
  description: PropTypes.string,
});

GaugeSelectorOption.propTypes = {
  style: PropTypes.object,
  option: GaugeSelectorOptionPropType,
  disabled: PropTypes.bool,
  isActive: PropTypes.bool,
  isCurrent: PropTypes.bool,
  isHovered: PropTypes.bool,
  onHover: PropTypes.func,
  onHoverEnd: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

// eslint-disable-next-line import/no-default-export
export default memo(GaugeSelectorOption);
